import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Sparkles, Loader2, Save, CheckCircle2, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";

const Revisions = ({ revisions, onView }) => (
  <aside className="rounded-3xl bg-white/60 border border-[#E6DEC3] p-5" data-testid="doc-revisions">
    <p className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Version & revision log</p>
    <ul className="mt-3 space-y-3 max-h-[60vh] overflow-y-auto">
      {revisions.map((r) => (
        <li key={r.version} className="text-sm" data-testid={`doc-revision-${r.version}`}>
          <button onClick={() => onView(r)} className="text-left hover:text-[#C87D53]">
            <b className="text-[#2C4A3E]">v{r.version}</b> · {r.change_note}
          </button>
          <p className="text-xs text-stone-500">{r.author} · {new Date(r.created_at).toLocaleString()}{r.approved_at && <span className="ml-1 text-[#6B9080] font-medium">· Approved</span>}</p>
        </li>
      ))}
      {!revisions.length && <li className="text-sm text-stone-400">No versions yet.</li>}
    </ul>
  </aside>
);

export default function DocEditor() {
  const { docKey } = useParams();
  const [doc, setDoc] = useState(null);
  const [content, setContent] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [pending, setPending] = useState(null);

  const load = useCallback(() => http.get(`/admin/library/${docKey}`).then((r) => { setDoc(r.data); setContent(r.data.content); }), [docKey]);

  const poll = useCallback(async (r) => {
    setBusy(true); setPreview(true);
    try {
      while (r.data.status === "running") {
        setContent(r.data.content);
        setProgress(`${r.data.parts_done}/${r.data.parts_total}`);
        await new Promise((res) => setTimeout(res, 2500));
        r = await http.get(`/admin/library/${docKey}/draft`);
      }
      if (r.data.status !== "done") throw new Error(r.data.error);
      setContent(r.data.content); setPending(null);
      setNote(r.data.kind === "template" ? "Generated template" : "Drafted with Aria");
      toast.success("Draft ready. Review it, then save a new version.");
    } catch { toast.error("Aria couldn't draft this document. Try again."); }
    finally { setBusy(false); setProgress(""); }
  }, [docKey]);

  useEffect(() => {
    load().then(() => http.get(`/admin/library/${docKey}/draft`)).then((r) => {
      if (r.data.status === "running") poll(r);
      else if (r.data.status === "done") setPending(r.data);
    });
  }, [load, poll, docKey]);

  const draft = async () => {
    if (content && !window.confirm("Replace the current text with a new draft from Aria?")) return;
    setContent(""); setPending(null);
    try { poll(await http.post(`/admin/library/${docKey}/draft`, null, { params: { fresh: true } })); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const discard = async () => { await http.delete(`/admin/library/${docKey}/draft`); setPending(null); };
  const save = async () => {
    try { const r = await http.put(`/admin/library/${docKey}`, { content, change_note: note }); toast.success(`Saved as version ${r.data.version}`); setNote(""); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setStatus = async (status) => {
    try { await http.post(`/admin/library/${docKey}/status`, { status }); toast.success(status === "approved" ? "Approved & published" : "Unpublished"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!doc) return <p className="text-center py-20 text-stone-500">Loading…</p>;
  const dirty = content !== doc.content;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="doc-editor-page">
      <Link to={doc.kind === "course" ? `/admin/courses/${doc.state}/${doc.course.id}` : "/admin/library"} className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#2C4A3E]" data-testid="doc-back-link"><ArrowLeft className="w-4 h-4" /> {doc.kind === "course" ? doc.course.title : "Curriculum Library"}</Link>
      <div className="flex flex-wrap justify-between items-end gap-4 mt-5">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#2C4A3E]" data-testid="doc-title">{doc.title}</h1>
          <p className="text-stone-500 mt-1 text-sm">{doc.state?.toUpperCase()} · {doc.visibility === "private" ? "Teachers & parents only" : "Public"} · {doc.summary} · <span data-testid="doc-status">{doc.status === "missing" ? "Not started" : `${doc.status} v${doc.version}`}</span></p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={draft} disabled={busy} data-testid="doc-draft-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C87D53] text-white text-sm hover:bg-[#B36C44] disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? `Aria is writing…${progress && progress !== "0/1" ? ` (${progress} sections)` : ""}` : "Draft with Aria"}
          </button>
          {doc.status === "draft" && !dirty && <button onClick={() => setStatus("approved")} data-testid="doc-approve-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C4A3E] text-white text-sm"><CheckCircle2 className="w-4 h-4" /> Approve & publish</button>}
          {doc.approved_at && <button onClick={() => setStatus("unpublished")} data-testid="doc-unpublish-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E6DEC3] text-sm"><EyeOff className="w-4 h-4" /> Unpublish</button>}
        </div>
      </div>
      <div className="grid lg:grid-cols-4 gap-6 mt-8">
        {pending && !busy && (
          <div className="lg:col-span-4 flex flex-wrap items-center gap-3 rounded-2xl bg-[#E3EBE5] px-5 py-3 text-sm text-[#2C4A3E]" data-testid="doc-pending-banner">
            <Sparkles className="w-4 h-4" /> {pending.kind === "template" ? "A generated template" : "An unsaved draft from Aria"} is ready ({new Date(pending.finished_at).toLocaleString()}).
            <button onClick={() => { setContent(pending.content); setPreview(true); setNote(pending.kind === "template" ? "Generated template" : "Drafted with Aria"); setPending(null); }}
              data-testid="doc-pending-load-btn" className="ml-auto px-4 py-1.5 rounded-full bg-[#2C4A3E] text-white">Load draft</button>
            <button onClick={discard} data-testid="doc-pending-discard-btn" className="px-4 py-1.5 rounded-full border border-[#2C4A3E]/30">Discard</button>
          </div>
        )}
        <div className="lg:col-span-3 space-y-3">
          <div className="flex p-1 rounded-full bg-[#F1EADB] text-sm w-fit">
            {[["Preview", true], ["Edit", false]].map(([l, v]) => (
              <button key={l} onClick={() => setPreview(v)} data-testid={`doc-${l.toLowerCase()}-tab`} className={`px-4 py-1 rounded-full ${preview === v ? "bg-[#2C4A3E] text-white" : "text-stone-600"}`}>{l}</button>
            ))}
          </div>
          {preview ? (
            <div className={`lesson-md rounded-3xl border border-[#E6DEC3] bg-white p-8 min-h-[50vh] text-stone-700 ${busy ? "cursor-blink" : ""}`} data-testid="doc-preview">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || "_Nothing yet. Click **Draft with Aria** to begin._"}</ReactMarkdown>
            </div>
          ) : (
            <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={28} data-testid="doc-content-textarea"
              className="w-full p-5 rounded-3xl bg-white border border-[#E6DEC3] font-mono text-xs leading-relaxed focus:outline-none focus:border-[#2C4A3E]" />
          )}
          <div className="flex flex-wrap gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed? (revision note)" data-testid="doc-change-note-input"
              className="flex-1 min-w-[220px] px-4 py-2.5 rounded-full bg-white border border-[#E6DEC3] text-sm focus:outline-none focus:border-[#2C4A3E]" />
            <button onClick={save} disabled={busy || !content.trim() || !dirty} data-testid="doc-save-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2C4A3E] text-white text-sm disabled:opacity-40">
              <Save className="w-4 h-4" /> Save new version
            </button>
          </div>
          {dirty && !busy && <p className="text-xs text-[#C87D53]" data-testid="doc-unsaved-hint">Unsaved changes. Save a new version before approving.</p>}
        </div>
        <Revisions revisions={doc.revisions} onView={(r) => { if (!dirty || window.confirm("Discard unsaved changes?")) { setContent(r.content); setPreview(true); setNote(`Restored from v${r.version}`); } }} />
      </div>
    </div>
  );
}
